import type {
    AnalyticsButtonInfo,
    EventSeriesData,
    TicketTimeCtaData,
} from '@beat-venues/common-types';
import { urlUtil } from '@beat-venues/utils';
import { useTranslation } from 'next-i18next';
import { type VariantProps, tv } from 'tailwind-variants';

import {
    Button,
    Heading,
    Image,
    StatusLabel,
    Text,
    TextLink,
    TextLinkProps,
} from '../../../atoms';
import type { CommonProps } from '../../../types';
import { useAnalytics } from '../../../useAnalytics';

import type { JSX } from 'react';

export interface EventCompactCardItemProps
    extends CommonProps,
        Pick<TextLinkProps, 'href' | 'locale'>,
        VariantProps<typeof EventCompactCardItemStyles>,
        Pick<EventSeriesData, 'date' | 'slug' | 'image'> {
    name?: string;
    venueId?: string;
    ctaButton?: TicketTimeCtaData;
    ctaTextLink: { url?: string; label: string };
    onBuyTicketClick?: (value: AnalyticsButtonInfo) => void;
    eventId: string;
    buttonVariant?: 'primary' | 'mastercard';
    status?: string;
    venueName?: string;
    linkProps: Pick<TextLinkProps, 'href' | 'locale'>;
    endSalesDate?: string;
    loungeDescription?: string;
}

export const EventCompactCardItemStyles = tv({
    slots: {
        root: 'relative bg-gray-500 rounded-md flex max-md:flex-col gap-6 overflow-hidden md:h-[140px]',
        statusLabel: 'absolute top-1 left-1',
        infoWrapper: 'grow my-auto max-md:px-6 flex flex-col',
        moreInfoCta: 'self-start',
        buttonWrapper:
            'min-w-[300px] flex flex-col justify-center items-center gap-0 md:mr-2 mb-2 md:mb-0 md:gap-4 mt-4',
        button: 'self-center mx-2 lg:mx-4 shrink-0 relative max-md:mb-6',
    },
});

export const EventCompactCardItem = ({
    className,
    image,
    name,
    eventId,
    date,
    status,
    venueId,
    venueName,
    ctaTextLink,
    ctaButton,
    linkProps,
    endSalesDate,
    loungeDescription,
    buttonVariant = 'primary',
    testId = 'event-card',
    onBuyTicketClick,
}: EventCompactCardItemProps): JSX.Element => {
    const { t } = useTranslation(['global', 'event']);

    const camefromParam = useAnalytics();

    const {
        root,
        statusLabel: statusLabelStyle,
        button,
        buttonWrapper,
        infoWrapper,
        moreInfoCta,
    } = EventCompactCardItemStyles();

    return (
        <div className={root({ className })} data-testid={testId}>
            <div className="lg:w-[249px]">
                {status && (
                    <StatusLabel className={statusLabelStyle()}>
                        {status}
                    </StatusLabel>
                )}
                <div className="aspect-video h-full">
                    {image && (
                        <Image
                            alt={image.alt}
                            aspectRatio={image.aspectRatio}
                            className="h-full object-cover"
                            height="1080"
                            priority={true}
                            rounded="none"
                            src={image.url}
                            width="1920"
                        />
                    )}
                </div>
            </div>
            <div className={infoWrapper()}>
                <Heading as="h2" className="mb-0" size="h5">
                    {name}
                </Heading>
                {date && <Text>{date}</Text>}

                <Text>
                    {loungeDescription
                        ? loungeDescription
                        : venueId
                        ? t(`venues.${venueId}.name`, {
                              ns: 'global',
                          })
                        : venueName}
                </Text>

                {ctaTextLink?.url && (
                    <TextLink
                        className={moreInfoCta()}
                        prefetch={true}
                        size="sm"
                        underline={true}
                        {...linkProps}
                    >
                        {ctaTextLink.label}
                    </TextLink>
                )}
            </div>
            {ctaButton && (
                <div className={buttonWrapper()}>
                    <Button
                        className={button()}
                        disabled={ctaButton.isDisabled}
                        href={
                            ctaButton.url &&
                            urlUtil.buildBuyButtonUrl(
                                ctaButton.url,
                                camefromParam
                            )
                        }
                        intent={buttonVariant}
                        onClick={() => {
                            const buttonInfo: AnalyticsButtonInfo = {
                                event: 'button_click',
                                id: eventId,
                                name: 'buy tickets',
                                text: t(`ctaStatusLabels.${ctaButton.label}`, {
                                    ns: 'event',
                                }),
                                category: 'event ticket link',
                                type: 'cta',
                                target_url: ctaButton?.url || '',
                            };
                            if (onBuyTicketClick) {
                                onBuyTicketClick(buttonInfo);
                            }
                        }}
                    >
                        <span>
                            {/\d/.test(ctaButton.label)
                                ? ctaButton.label
                                : t(`ctaStatusLabels.${ctaButton.label}`, {
                                      ns: 'event',
                                  })}
                        </span>
                    </Button>
                    {endSalesDate && !ctaButton.isDisabled && (
                        <Text className="text-vn-brand-500">
                            {t('date.endPresales', {
                                date: endSalesDate,
                                ns: 'event',
                            })}
                        </Text>
                    )}
                </div>
            )}
        </div>
    );
};
